import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';
import { Package, Plus, X, Check } from 'lucide-react';

function formatPrice(sek) {
  if (sek == null || sek === 0) return '—';
  return `${sek.toLocaleString('sv-SE')} kr`;
}

function calcDiscountPct(monthly, yearly) {
  if (!monthly || !yearly) return null;
  const fullYearly = monthly * 12;
  if (fullYearly <= yearly) return null;
  return Math.round(((fullYearly - yearly) / fullYearly) * 100);
}

const emptyForm = {
  code: '',
  name: '',
  description: '',
  max_users: '',
  max_cases_per_year: '',
  max_verksamheter: '',
  ocn_enabled: false,
  ai_enabled: false,
  monthly_price_sek: 0,
  yearly_price_sek: 0,
  implementation_fee_sek: 0,
  is_active: true,
  sort_order: 50,
};

// ── Plan-formulär (skapa/redigera) ────────────────────────────────────────────

const PlanForm = ({ open, onClose, plan, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!plan;

  useEffect(() => {
    if (plan) {
      setForm({
        code: plan.code,
        name: plan.name,
        description: plan.description || '',
        max_users: plan.max_users ?? '',
        max_cases_per_year: plan.max_cases_per_year ?? '',
        max_verksamheter: plan.max_verksamheter ?? '',
        ocn_enabled: plan.ocn_enabled,
        ai_enabled: plan.ai_enabled,
        monthly_price_sek: plan.monthly_price_sek || 0,
        yearly_price_sek: plan.yearly_price_sek || 0,
        implementation_fee_sek: plan.implementation_fee_sek || 0,
        is_active: plan.is_active,
        sort_order: plan.sort_order || 50,
      });
    } else {
      setForm(emptyForm);
    }
  }, [plan, open]);

  if (!open) return null;

  const setField = (key) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [key]: val });
  };

  const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        max_users: numOrNull(form.max_users),
        max_cases_per_year: numOrNull(form.max_cases_per_year),
        max_verksamheter: numOrNull(form.max_verksamheter),
        monthly_price_sek: Number(form.monthly_price_sek) || 0,
        yearly_price_sek: Number(form.yearly_price_sek) || 0,
        implementation_fee_sek: Number(form.implementation_fee_sek) || 0,
        sort_order: Number(form.sort_order) || 0,
      };
      if (isEdit) {
        const { code, ...patchData } = payload;
        await api.patch(`/admin/plans/${plan.id}`, patchData);
        toast.success('Plan uppdaterad');
      } else {
        await api.post('/admin/plans', payload);
        toast.success('Plan skapad');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte spara plan');
    } finally {
      setSubmitting(false);
    }
  };

  const discount = calcDiscountPct(form.monthly_price_sek, form.yearly_price_sek);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.25)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{isEdit ? 'Redigera plan' : 'Ny plan'}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        {isEdit && (
          <div className="mb-4 p-3 rounded text-sm" style={{ background: '#fffbeb', borderLeft: '4px solid #F9CF3F' }}>
            <strong>OBS:</strong> Ändringar i denna plan påverkar INTE befintliga avtal — de har sina egna limits från avtalstidpunkten.
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-1">Kod *</label>
              <input
                required
                type="text"
                value={form.code}
                onChange={setField('code')}
                disabled={isEdit}
                placeholder="t.ex. growth"
                className="w-full border rounded px-3 py-2 text-sm font-mono disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Namn *</label>
              <input
                required type="text" value={form.name} onChange={setField('name')}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Beskrivning</label>
            <textarea
              rows={2}
              value={form.description}
              onChange={setField('description')}
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>

          <fieldset className="border rounded p-3">
            <legend className="text-sm font-semibold px-2">Limits (tomt = obegränsat)</legend>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Max användare</label>
                <input type="number" min="0" value={form.max_users} onChange={setField('max_users')} className="w-full border rounded px-2 py-1" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Max case/år</label>
                <input type="number" min="0" value={form.max_cases_per_year} onChange={setField('max_cases_per_year')} className="w-full border rounded px-2 py-1" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Max verksamheter</label>
                <input type="number" min="0" value={form.max_verksamheter} onChange={setField('max_verksamheter')} className="w-full border rounded px-2 py-1" />
              </div>
            </div>
          </fieldset>

          <fieldset className="border rounded p-3">
            <legend className="text-sm font-semibold px-2">Features</legend>
            <div className="space-y-1 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.ocn_enabled} onChange={setField('ocn_enabled')} />
                OCN-validering
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.ai_enabled} onChange={setField('ai_enabled')} />
                AI-funktioner
              </label>
            </div>
          </fieldset>

          <fieldset className="border rounded p-3">
            <legend className="text-sm font-semibold px-2">Priser (SEK)</legend>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Månadspris</label>
                <input type="number" min="0" value={form.monthly_price_sek} onChange={setField('monthly_price_sek')} className="w-full border rounded px-2 py-1" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Årspris {discount != null && <span className="text-green-700">(-{discount}%)</span>}</label>
                <input type="number" min="0" value={form.yearly_price_sek} onChange={setField('yearly_price_sek')} className="w-full border rounded px-2 py-1" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Implementation</label>
                <input type="number" min="0" value={form.implementation_fee_sek} onChange={setField('implementation_fee_sek')} className="w-full border rounded px-2 py-1" />
              </div>
            </div>
          </fieldset>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.is_active} onChange={setField('is_active')} />
              Aktiv (kan väljas för nya avtal)
            </label>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Sort order</label>
              <input type="number" value={form.sort_order} onChange={setField('sort_order')} className="w-full border rounded px-2 py-1" />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded hover:bg-gray-50">Avbryt</button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded text-black font-semibold disabled:opacity-50"
              style={{ background: '#F9CF3F' }}
            >
              {submitting ? 'Sparar...' : (isEdit ? 'Spara ändringar' : 'Skapa plan')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Huvud-sida ────────────────────────────────────────────────────────────────

const SubscriptionPlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editPlan, setEditPlan] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/plans');
      setPlans(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error('Kunde inte hämta planer');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (plan) => {
    if (!window.confirm(`Avaktivera planen "${plan.name}"? Den döljs för nya avtal, befintliga avtal påverkas inte.`)) return;
    try {
      await api.delete(`/admin/plans/${plan.id}`);
      toast.success('Plan avaktiverad');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte avaktivera plan');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="subscription-plans-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Package size={24} />
              Prenumerationsplaner
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Hantera vilka plan-tier kunder kan välja och deras priser
            </p>
          </div>
          <button
            onClick={() => { setEditPlan(null); setFormOpen(true); }}
            className="flex items-center gap-1 px-4 py-2 rounded font-semibold text-black"
            style={{ background: '#F9CF3F' }}
          >
            <Plus size={16} />
            Ny plan
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Laddar...</p>
        ) : plans.length === 0 ? (
          <p className="text-sm text-gray-500">Inga planer ännu.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {plans.map((p) => {
              const discount = calcDiscountPct(p.monthly_price_sek, p.yearly_price_sek);
              return (
                <div
                  key={p.id}
                  className={`bg-white rounded border p-5 ${p.is_active ? '' : 'opacity-60'}`}
                  data-testid={`plan-card-${p.code}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h2 className="text-lg font-bold">{p.name}</h2>
                      <p className="text-xs text-gray-500 font-mono">{p.code}</p>
                    </div>
                    <span
                      className="px-2 py-0.5 rounded text-xs font-semibold"
                      style={{ background: p.is_active ? '#dcfce7' : '#f3f4f6', color: p.is_active ? '#166534' : '#374151' }}
                    >
                      {p.is_active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </div>

                  {p.description && <p className="text-sm text-gray-700 mb-3">{p.description}</p>}

                  <div className="text-sm space-y-1 mb-3">
                    <p><span className="text-gray-500">Max användare:</span> <strong>{p.max_users ?? 'Obegränsat'}</strong></p>
                    <p><span className="text-gray-500">Max case/år:</span> <strong>{p.max_cases_per_year ?? 'Obegränsat'}</strong></p>
                    <p><span className="text-gray-500">Max verksamheter:</span> <strong>{p.max_verksamheter ?? 'Obegränsat'}</strong></p>
                    <p className="flex gap-3 mt-2">
                      <span className="flex items-center gap-1">{p.ocn_enabled ? <Check size={14} className="text-green-700" /> : <X size={14} className="text-gray-400" />}OCN</span>
                      <span className="flex items-center gap-1">{p.ai_enabled ? <Check size={14} className="text-green-700" /> : <X size={14} className="text-gray-400" />}AI</span>
                    </p>
                  </div>

                  <div className="border-t pt-3 text-sm space-y-0.5">
                    <p><span className="text-gray-500">Månadspris:</span> <strong>{formatPrice(p.monthly_price_sek)}</strong></p>
                    <p>
                      <span className="text-gray-500">Årspris:</span> <strong>{formatPrice(p.yearly_price_sek)}</strong>
                      {discount != null && <span className="ml-2 text-green-700 text-xs">(-{discount}%)</span>}
                    </p>
                    {p.implementation_fee_sek > 0 && (
                      <p><span className="text-gray-500">Implementation:</span> <strong>{formatPrice(p.implementation_fee_sek)}</strong></p>
                    )}
                  </div>

                  <div className="border-t mt-3 pt-3 flex items-center justify-between text-sm">
                    <span className="text-gray-500">
                      {p.active_contracts_count || 0} aktivt avtal
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditPlan(p); setFormOpen(true); }}
                        className="font-semibold hover:underline"
                      >
                        Redigera
                      </button>
                      {p.is_active && (
                        <button
                          onClick={() => handleDelete(p)}
                          className="font-semibold text-red-700 hover:underline"
                        >
                          Avaktivera
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <PlanForm
          open={formOpen}
          onClose={() => { setFormOpen(false); setEditPlan(null); }}
          plan={editPlan}
          onSaved={load}
        />
      </div>
    </DashboardLayout>
  );
};

export default SubscriptionPlansPage;
